import UtilsService from './UtilsService';
import CookiesService from './CookiesService';
import { STORAGE_CONFIG, CROSS_DOMAIN_KEYS } from './StorageService.config';
import { trackError } from '../utils/error.utils';

type TStorageType = 'localStorage' | 'sessionStorage';

interface Params {
    key: string;
    data: unknown;
    shouldThrowError?: boolean;
    storageType?: TStorageType;
    isCrossDomainKey?: boolean;
}

/**
 * StorageService - Unified browser storage with cross-domain support
 *
 * Provides localStorage/sessionStorage operations with automatic cookie backup for cross domain data (signup-related data, etc.).
 * When data is marked as cross domain (via flag or auto-detection), it's stored in both browser storage
 * AND cookies, enabling access from different subdomains
 */
export default class StorageService {
    private static readonly COOKIE_PREFIX = 'hb_marketing_storage_';

    private static isCrossDomainKey(key: string): boolean {
        return CROSS_DOMAIN_KEYS.includes(key);
    }

    private static getCookieName(key: string, storageType: TStorageType): string {
        return `${this.COOKIE_PREFIX}${storageType}_${key}`;
    }


    private static getCookieExpirationDays(storageType: TStorageType): number {
        return storageType === 'localStorage'
            ? STORAGE_CONFIG.localStorageExpirationDays
            : STORAGE_CONFIG.sessionStorageExpirationDays;
    }

    private static setCookieItem(key: string, data: string, storageType: TStorageType, shouldThrowError: boolean): void {
        try {
            const cookieName = this.getCookieName(key, storageType);
            const expirationDays = this.getCookieExpirationDays(storageType);

            CookiesService.setCookie({
                cookieName,
                cookieValue: data,
                expirationDays,
                domain: STORAGE_CONFIG.cookieDomain
            });
        } catch (err) {
            trackError(err, {
                message: `[StorageService] setCookieItem: ${err.message}`,
                data: {
                    key,
                    storageType
                }
            });
            if (shouldThrowError) {
                throw err;
            }
        }
    }

    private static getCookieItem(key: string, storageType: TStorageType): unknown {
        try {
            const cookieName = this.getCookieName(key, storageType);
            const cookieValue = CookiesService.getCookie(cookieName);

            if (!cookieValue) {
                return null;
            }

            return JSON.parse(cookieValue);
        } catch (err) {
            trackError(err, {
                message: `[StorageService] getCookieItem: ${err.message}`,
                data: {
                    key,
                    storageType
                }
            });
            return null;
        }
    }

    private static removeCookieItem(key: string, storageType: TStorageType): void {
        try {
            const cookieName = this.getCookieName(key, storageType);
            CookiesService.deleteCookie(cookieName);
        } catch {
            return;
        }
    }

    static setItem({
        key,
        data,
        shouldThrowError = false,
        storageType = 'localStorage',
        isCrossDomainKey = false
    }: Params) {
        const shouldStoreInCookies = isCrossDomainKey || this.isCrossDomainKey(key);

        try {
            if (UtilsService.isBrowser() && window[storageType]) {
                const stringifiedData = JSON.stringify(data);
                window[storageType].setItem(key, stringifiedData);
                if (shouldStoreInCookies) {
                    this.setCookieItem(key, stringifiedData, storageType, false);
                }
            } else if (shouldThrowError) {
                throw new Error();
            }
        } catch (err) {
            if (shouldThrowError) {
                throw err;
            }
        }
    }

    static getItem(key: string, storageType: TStorageType = 'localStorage', isCrossDomainKey: boolean = false) {
        try {
            if (UtilsService.isBrowser() && window[storageType]) {
                if (isCrossDomainKey || this.isCrossDomainKey(key)) {
                    const cookieData = this.getCookieItem(key, storageType);
                    if (cookieData !== null) {
                        return cookieData;
                    }
                }
                return JSON.parse(window[storageType].getItem(key) || 'null');
            }
        } catch {
            return null;
        }
    }

    static removeItem(key: string, storageType: TStorageType = 'localStorage', isCrossDomainKey: boolean = false) {
        try {
            if (UtilsService.isBrowser() && window[storageType]) {
                window[storageType].removeItem(key);
                if (isCrossDomainKey || this.isCrossDomainKey(key)) {
                    this.removeCookieItem(key, storageType);
                }
            }
        } catch {
            return;
        }
    }
}
